import React, { useState, useEffect, useCallback } from 'react';
import ErrorKbModal from './ErrorKbModal';
import errorKbService from '../../services/errorKbService';
import errorSignature from '../../utils/errorSignature';
import { classifyStoryLine } from './messageLogUtils';

// Shared Error-Knowledge-Base wiring: loads the template's KB entries and renders a single "KB?"
// trigger for any message, plus the modal element. Clicking it shows the KB info when an entry
// exists, or (for superadmins) offers an Add button to create one. Used by the reconciliation list
// and the MessageLogPage so both read one implementation.
//
//   const { renderKbTriggerFor, kbModalElement } = useErrorKb(task?.syncTemplateId, isSuperAdmin);
//   ... {renderKbTriggerFor(record.comment)} ... {kbModalElement}
export default function useErrorKb(syncTemplateId, isSuperAdmin) {
    const [kbBySignature, setKbBySignature] = useState({});
    const [kbModal, setKbModal] = useState({ open: false, entry: null, sampleMessage: '', signature: '' });

    const loadKb = useCallback(async (templateId) => {
        if (!templateId) return;
        try {
            const data = await errorKbService.list(templateId);
            const map = {};
            (data.entries || []).forEach(e => { map[e.errorSignature] = e; });
            setKbBySignature(map);
        } catch (_) { /* KB is non-essential — ignore load failures */ }
    }, []);

    useEffect(() => { if (syncTemplateId) loadKb(syncTemplateId); }, [syncTemplateId, loadKb]);

    const openKbFor = (message, entry) =>
        setKbModal({ open: true, entry: entry || null, sampleMessage: message, signature: errorSignature(message) });

    // Single "KB?" trigger. Clicking it opens the modal, which shows the KB info when an entry
    // exists or offers an Add button (superadmins) when none does yet. Knowledge is matched by the
    // error's normalised signature, so the same entry covers every occurrence.
    // Shown when an entry exists (any user can view) or the user is a superadmin (can view/add).
    const renderKbTriggerFor = (message) => {
        if (!message || !message.trim()) return null;
        // No KB on clean success lines (green, e.g. "… → 200") — nothing to explain or fix.
        if (classifyStoryLine(message) === 'success') return null;
        const entry = kbBySignature[errorSignature(message)];
        if (!entry && !isSuperAdmin) return null;
        return (
            <button type="button" className={`ekb-trigger${entry ? ' ekb-help' : ''}`}
                title={entry ? 'Knowledge Base: what this means / how to fix it' : 'Knowledge Base: no entry yet — click to view or add'}
                onClick={() => openKbFor(message, entry || null)}>KB?</button>
        );
    };

    const kbModalElement = (
        <ErrorKbModal
            isOpen={kbModal.open}
            onClose={() => setKbModal({ open: false, entry: null, sampleMessage: '', signature: '' })}
            entry={kbModal.entry}
            sampleMessage={kbModal.sampleMessage}
            errorSignature={kbModal.signature}
            syncTemplateId={syncTemplateId}
            canEdit={!!isSuperAdmin}
            onSaved={() => loadKb(syncTemplateId)}
        />
    );

    return { renderKbTriggerFor, kbModalElement, kbBySignature };
}
